import { css } from '@emotion/react'
import api from 'api'
import { ImpressaoAcompanhamentoMChatInput } from 'api/ImpressaoMChat'
import { Button, Heading, HFlow, Icon, ModalCloseButton, Text, Theme, useTheme, VFlow } from 'bold-ui'
import { useFlags } from 'config/useFlagsContext'
import { MChatRiscoEnum } from 'graphql/types.generated'
import { reportFormatDate, useHandleImpressao } from 'hooks/impressao/useHandleImpressao'
import { MouseEvent, useEffect, useMemo, useRef } from 'react'

import { MChatAccessDocLink } from '../components/MChatAccessDocLink'
import { convertMChatToPerguntaRespostaInput } from '../converter-mchat'
import { MChatResultadoView } from '../mchat-resultado/MChatResultadoView'
import { ImpressaoMchat, MChatFieldModel } from '../model-mchat'

interface MChatModalTabResultadoProps {
  values: MChatFieldModel | undefined
  atendProfId: ID
  cidadaoId: ID
  isEditing: boolean
  onClickVoltar: () => void
}

export function MChatModalTabResultado(props: Readonly<MChatModalTabResultadoProps>) {
  const { values, atendProfId, cidadaoId, isEditing, onClickVoltar } = props

  const { PILOTO_M_CHAT_RF_ENABLED } = useFlags()
  const theme = useTheme()
  const styles = useMemo(() => createStyles(theme), [theme])
  const backButtonRef = useRef<HTMLButtonElement>(null)

  const { handleImpressaoPath, handleImpressaoError } = useHandleImpressao<ImpressaoAcompanhamentoMChatInput>()

  const isRiscoMedio = values?.risco === MChatRiscoEnum.MEDIO

  const handlePrint = (atendProfId: ID, cidadaoId: ID, mchat: MChatFieldModel) => {
    const input: ImpressaoMchat = {
      atendProfId: atendProfId,
      cidadaoId: cidadaoId,
      resultado: mchat.resultado,
      risco: mchat.risco,
      aplicacao: convertMChatToPerguntaRespostaInput(mchat),
    }

    handleImpressaoPath({
      apiPath: api.mchat.imprimirMChat,
      values: input,
      file: `MChat.pdf`,
      dateFormat: reportFormatDate,
    }).catch(handleImpressaoError)
  }

  useEffect(() => {
    backButtonRef?.current?.focus()
  }, [])

  return (
    <VFlow>
      <HFlow alignItems='flex-start' justifyContent='space-between'>
        <Button kind='normal' size='small' style={styles.voltarButton} onClick={onClickVoltar} innerRef={backButtonRef}>
          <Icon icon='arrowLeft' style={styles.icons} />
          {isEditing ? 'Voltar à edição' : 'Editar a avaliação'}
        </Button>
        <ModalCloseButton />
      </HFlow>
      <VFlow style={styles.modalSectionBody}>
        <HFlow alignItems='center' justifyContent='space-between'>
          <Heading level={1}>Resultado M-CHAT-R™</Heading>
          {values && (
            <Button
              kind='normal'
              size='small'
              style={styles.imprimirButton}
              onClick={(event: MouseEvent<HTMLButtonElement>) => {
                event.stopPropagation()
                handlePrint(atendProfId, cidadaoId, values)
              }}
            >
              <Icon icon='printerOutline' style={styles.icons} />
              Imprimir resultado
            </Button>
          )}
        </HFlow>
        {values && <MChatResultadoView isInsideModal showEscala value={values} isRegistradoAgora={true} />}
        {isRiscoMedio && (
          <VFlow vSpacing={0.5}>
            <HFlow>
              <HFlow hSpacing={0.5} alignItems='center'>
                <Icon icon='exclamationTriangleOutline' fill='alert' />
                <Heading level={4} color='alert'>
                  Entrevista de seguimento
                </Heading>
              </HFlow>
              {!PILOTO_M_CHAT_RF_ENABLED && <MChatAccessDocLink />}
            </HFlow>
            <Text>
              {!PILOTO_M_CHAT_RF_ENABLED && 'Acesse o documento original para aplicar a avaliação. '}O teste é
              considerado positivo (risco para TEA) se a criança falhar em quaisquer 2 itens na Entrevista de
              Seguimento.
            </Text>
          </VFlow>
        )}
      </VFlow>
    </VFlow>
  )
}

const createStyles = (theme: Theme) => ({
  icons: css`
    margin-right: 0.5rem;
  `,
  voltarButton: css`
    margin-bottom: 1rem;
  `,
  imprimirButton: css`
    color: ${theme.pallete.primary.main};
    border-color: ${theme.pallete.primary.main};
  `,
  modalSectionBody: css`
    padding-right: 1rem;
  `,
})
